/**
 * Login Page
 * Authentication entry point with ISF branding
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, LogIn, AlertCircle } from 'lucide-react';
import api from '../services/api';

// Google icon component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  
  const { login, loginWithToken, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redirect to dashboard after login (not landing page)
  const from = location.state?.from?.pathname || '/dashboard';
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);
  
  // Check if Google Auth is configured
  useEffect(() => {
    const checkGoogleAuth = async () => {
      try {
        const response = await api.get('/auth/google/status');
        setGoogleEnabled(response.data?.enabled || false);
      } catch {
        setGoogleEnabled(false);
      }
    };
    checkGoogleAuth();
  }, []);
  
  // Handle OAuth callback token or error
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const refreshToken = params.get('refresh_token');
    const oauthError = params.get('error');
    const oauthEmail = params.get('email');
    
    // Clear URL params
    if (token || oauthError) {
      window.history.replaceState({}, document.title, '/login');
    }
    
    if (oauthError) {
      // Handle OAuth errors
      const errorMessages = {
        'user_not_found': `No account found for ${oauthEmail || 'this email'}. Please sign up first.`,
        'user_creation_failed': 'Failed to create account. Please try again.',
        'no_tenant_available': 'System not configured. Please contact administrator.',
        'token_exchange_failed': 'Authentication failed. Please try again.',
        'userinfo_failed': 'Could not retrieve user information. Please try again.',
        'oauth_error': 'Authentication error. Please try again.',
        'invalid_callback': 'Invalid authentication response. Please try again.',
      };
      setError(errorMessages[oauthError] || `Authentication error: ${oauthError}`);
      setIsGoogleLoading(false);
      return;
    }
    
    if (token) {
      // Store refresh token if provided
      if (refreshToken) {
        localStorage.setItem('isf_refresh_token', refreshToken);
      }
      // Login with the received token
      handleGoogleCallback(token);
    }
  }, []);
  
  const handleGoogleCallback = async (token) => {
    try {
      setIsGoogleLoading(true);
      await loginWithToken(token);
      // Navigation will happen via useEffect when isAuthenticated becomes true
    } catch (err) {
      setError('Google login failed. Please try again.');
      setIsGoogleLoading(false);
    }
  };
  
  const handleGoogleLogin = async () => {
    setError('');
    setIsGoogleLoading(true);
    
    try {
      const response = await api.get('/auth/google/login');
      if (response.data?.auth_url) {
        // Redirect to Google OAuth
        window.location.href = response.data.auth_url;
      } else {
        setError('Google login not available');
        setIsGoogleLoading(false);
      }
    } catch (err) {
      setError('Failed to initiate Google login');
      setIsGoogleLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      await login(email, password);
      // Navigation will happen via useEffect when isAuthenticated becomes true
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
      setIsLoading(false);
    }
  };
  
  // Demo credentials quick fill
  const fillDemoCredentials = (role) => {
    if (role === 'admin') {
      setEmail('admin@indianstreetfood.com');
      setPassword('admin123');
    } else if (role === 'manager') {
      setEmail('manager@indianstreetfood.com');
      setPassword('manager123');
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0a] p-4">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl border-0 relative z-10">
        <CardHeader className="space-y-1 text-center pb-2">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src="/logo-isf.png"
              alt="Indian Street Food & Co"
              className="h-20 object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-light text-gray-900 tracking-wide">
            Staff Portal
          </CardTitle>
          <CardDescription className="text-gray-500">
            Sign in to access the management system
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive" className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@indianstreetfood.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="border-gray-300 focus:border-[#c4a35a] focus:ring-[#c4a35a]"
                data-testid="login-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
                className="border-gray-300 focus:border-[#c4a35a] focus:ring-[#c4a35a]"
                data-testid="login-password-input"
              />
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#c4a35a] hover:bg-[#b39349] text-black font-medium h-12"
              disabled={isLoading}
              data-testid="login-submit-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <LogIn className="mr-2 h-4 w-4" />
                  Sign In
                </>
              )}
            </Button>
          </form>
          
          {/* Google Sign-In - Hidden for now */}
          {/* 
          {googleEnabled && (
            <div className="mt-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-400">Or continue with</span>
                </div>
              </div>
              
              <Button
                type="button"
                variant="outline"
                className="w-full mt-4 h-12 border-gray-300 hover:bg-gray-50"
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading}
                data-testid="google-login-button"
              >
                {isGoogleLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <GoogleIcon />
                )}
                <span className="ml-2">Sign in with Google</span>
              </Button>
            </div>
          )}
          */}
          
          {/* Demo Credentials */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-gray-400 text-center mb-3 uppercase tracking-wider">
              Demo Access
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1 text-xs border-gray-300 hover:bg-gray-50"
                onClick={() => fillDemoCredentials('admin')}
                data-testid="demo-admin-button"
              >
                Admin
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1 text-xs border-gray-300 hover:bg-gray-50"
                onClick={() => fillDemoCredentials('manager')}
                data-testid="demo-manager-button"
              >
                Manager
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="absolute bottom-4 text-center text-white/30 text-xs">
        © {new Date().getFullYear()} Indian Street Food & Co
      </div>
    </div>
  );
}
